import { getActiveTokenForProvider } from './smartContentGenerationUtils';

const REDACTION_API_CONFIG = {
  claude: {
    endpoint: '/smart-content/smart_content/redaction/generate_text/claude',
    name: 'Claude',
  },
  gpt: {
    endpoint: '/smart-content/smart_content/redaction/generate_text/gpt',
    name: 'GPT',
  },
  gpt_stream: {
    endpoint: '/smart-content/smart_content/redaction/generate_text/gpt/stream',
    name: 'GPT',
  },
  perplexity: {
    endpoint: '/smart-content/smart_content/redaction/generate_text/perplexity',
    name: 'Perplexity',
  },
  gemini: {
    endpoint: '/smart-content/smart_content/redaction/generate_text/gemini',
    name: 'Gemini',
  },
  both: {
    endpoint: '/smart-content/smart_content/redaction/generate_text/multi_llm',
    name: 'GPT + Claude',
  },
};

const ENRICHMENT_API_CONFIG = {
  claude: {
    endpoint: '/smart-content/smart_content/enrichissement/claude',
    name: 'Claude',
  },
  gemini: {
    endpoint: '/smart-content/smart_content/enrichissement/gemini',
    name: 'Gemini',
  },
};

const DEFAULT_MODELS_BY_PROVIDER = {
  openai: 'GPT_4_1',
  claude: 'CLAUDE_SONNET_4_6',
  perplexity: 'SONAR',
  gemini: 'GEMINI_2_5_PRO',
};

const getUserIaConfigsFromLocalStorage = () => {
  try {
    const rawUser = localStorage.getItem('user');
    if (!rawUser) return [];
    const parsed = JSON.parse(rawUser);
    const iaConfigs = parsed?.user?.iaConfigs || parsed?.iaConfigs;
    return Array.isArray(iaConfigs) ? iaConfigs : [];
  } catch {
    return [];
  }
};

const normalizeModelForApi = (model) => {
  if (!model) return '';
  return String(model)
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
};

const getActiveIaConfigForProvider = (iaProvider) => {
  const providerKey = String(iaProvider || '').toLowerCase();
  if (!providerKey) return null;
  const iaConfigs = getUserIaConfigsFromLocalStorage();
  return (
    iaConfigs.find(
      (c) =>
        c?.active === true &&
        String(c?.iaProvider || '').toLowerCase() === providerKey
    ) || null
  );
};

const getModelForProviderInApi = (iaProvider) => {
  const providerKey = String(iaProvider || '').toLowerCase();
  const activeConfig = getActiveIaConfigForProvider(providerKey);
  const activeModel = normalizeModelForApi(activeConfig?.iaModel);
  if (activeModel) return activeModel;
  if (providerKey === 'openai') return DEFAULT_MODELS_BY_PROVIDER.openai;
  return DEFAULT_MODELS_BY_PROVIDER[providerKey] || '';
};

export const buildRedactionUrl = (selectedLlm, { isEnrichment = false } = {}) => {
  let llm = String(selectedLlm || '').toLowerCase();
  if (isEnrichment && !ENRICHMENT_API_CONFIG[llm]) {
    llm = 'claude';
  }
  const config = isEnrichment ? ENRICHMENT_API_CONFIG[llm] : REDACTION_API_CONFIG[llm];
  if (!config) return '';

  const params = new URLSearchParams();
  params.set('maxTokens', '10000');
  if (!isEnrichment) {
    params.set('temperature', '0.3');
  }

  if (llm === 'gpt') {
    params.set('chatGPTModel', getModelForProviderInApi('openai'));
    params.set('reasoning', 'MEDIUM');
  } else if (llm === 'claude') {
    params.set('claudeModel', getModelForProviderInApi('claude'));
  } else if (llm === 'perplexity') {
    params.set('perplexityModel', getModelForProviderInApi('perplexity'));
  } else if (llm === 'gemini') {
    params.set('geminiModel', getModelForProviderInApi('gemini'));
  } else if (llm === 'both') {
    params.set('chatGPTModel', 'GPT_5_6');
    params.set('claudeModel', 'CLAUDE_OPUS_5_5');
  }

  return `${config.endpoint}?${params.toString()}`;
};

export const buildGptStreamUrl = () => {
  const params = new URLSearchParams();
  params.set('maxTokens', '10000');
  params.set('temperature', '0.3');
  params.set('chatGPTModel', 'GPT_5_6');
  params.set('reasoning', 'MEDIUM');
  params.set('activeWebSearch', 'false');
  return `${REDACTION_API_CONFIG.gpt_stream.endpoint}?${params.toString()}`;
};

export const buildIaHeadersForRedactionText = (selectedLlm) => {
  const llm = String(selectedLlm || '').toLowerCase();
  const headers = {};

  const pushHeader = (iaProvider, headerName) => {
    const token = getActiveTokenForProvider(iaProvider);
    if (token) headers[headerName] = token;
  };

  if (llm === 'gpt') {
    pushHeader('openai', 'X-OpenAI-Api-Key');
  } else if (llm === 'claude') {
    pushHeader('claude', 'X-Anthropic-Api-Key');
  } else if (llm === 'perplexity') {
    pushHeader('perplexity', 'X-Perplexity-Api-Key');
  } else if (llm === 'gemini') {
    pushHeader('gemini', 'X-Gemini-Api-Key');
  } else if (llm === 'both') {
    pushHeader('openai', 'X-OpenAI-Api-Key');
    pushHeader('claude', 'X-Anthropic-Api-Key');
  }

  return headers;
};
